type EventInput = {
  id: string;
  name: string;
  startDate: Date | null;
  endDate: Date | null;
  location: string | null;
  organizer: {id: string; name: string} | null | undefined;
  categories?: {prices?: {amount: number; currency: string}[]}[];
};

type EventSchemaOptions = {
  siteUrl: string;
  description?: string | null;
};

type PlaceSchema = {
  '@type': 'Place';
  name: string;
};

type OrganizerSchema = {
  '@type': 'Organization';
  name: string;
  url: string;
};

type OfferSchema = {
  '@type': 'Offer';
  url: string;
  price: string;
  priceCurrency: string;
  availability: string;
};

type EventSchema = {
  '@context': 'https://schema.org';
  '@type': 'Event';
  name: string;
  url: string;
  image: string;
  eventAttendanceMode: string;
  eventStatus: string;
  startDate?: string;
  endDate?: string;
  location?: PlaceSchema;
  organizer?: OrganizerSchema;
  offers?: OfferSchema;
  description?: string;
};

/**
 * Formats a Date as a date-only ISO 8601 string (YYYY-MM-DD).
 * Avoids the midnight-UTC bug where toISOString() emits a time
 * component that misrepresents the event's local date.
 */
function formatDateOnly(date: Date): string {
  const y = date.getUTCFullYear();
  const m = String(date.getUTCMonth() + 1).padStart(2, '0');
  const d = String(date.getUTCDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

/**
 * Finds the lowest price across all category price tiers.
 * Returns null when no prices exist.
 */
function findLowestPrice(
  categories: EventInput['categories'],
): {amount: number; currency: string} | null {
  if (!categories) return null;
  let lowest: {amount: number; currency: string} | null = null;
  for (const cat of categories) {
    for (const price of cat.prices ?? []) {
      if (!lowest || price.amount < lowest.amount) {
        lowest = {amount: price.amount, currency: price.currency};
      }
    }
  }
  return lowest;
}

/**
 * Builds a Google-valid Event JSON-LD object from event data.
 * Emits only fields that have values — optional properties are
 * omitted entirely (not set to null) so JSON.stringify produces
 * a clean schema with no null entries.
 */
export function buildEventSchema(
  event: EventInput,
  options: EventSchemaOptions,
): EventSchema {
  const {siteUrl, description} = options;
  const eventUrl = `${siteUrl}/event/${event.id}`;

  const schema: EventSchema = {
    '@context': 'https://schema.org',
    '@type': 'Event',
    name: event.name,
    url: eventUrl,
    image: `${siteUrl}/api/og/event/${event.id}`,
    eventAttendanceMode: 'https://schema.org/OfflineEventAttendanceMode',
    eventStatus: 'https://schema.org/EventScheduled',
  };

  if (event.startDate) {
    schema.startDate = formatDateOnly(event.startDate);
  }

  if (event.endDate) {
    schema.endDate = formatDateOnly(event.endDate);
  }

  if (event.location) {
    schema.location = {
      '@type': 'Place',
      name: event.location,
    };
  }

  if (event.organizer) {
    schema.organizer = {
      '@type': 'Organization',
      name: event.organizer.name,
      url: `${siteUrl}/organizer/${event.organizer.id}`,
    };
  }

  const lowest = findLowestPrice(event.categories);
  schema.offers = {
    '@type': 'Offer',
    url: eventUrl,
    price: lowest ? String(lowest.amount / 100) : '0',
    priceCurrency: lowest?.currency ?? 'USD',
    availability: 'https://schema.org/InStock',
  };

  if (description) {
    schema.description = description;
  }

  return schema;
}
